import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";

/**
 * Client used for all queries, and gives us some sane default options. We set `staleTime` to
 * infinity so we only only refetch when we need to by default - we can override when we need to.
 */
function createReploQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Number.POSITIVE_INFINITY,
      },
    },
    mutationCache: new MutationCache({
      onError: (error, _, __, mutation) => {
        if (mutation.meta?.reploIgnoreDefaultErrorHandling) {
          return;
        }
        if (error instanceof TRPCClientError) {
          // Note (Noah, 2025-10-03): We have to dynamically import this to avoid a circular dependency,
          // since error handling needs to import TRPC routers (which we're currently constructing)
          void import("@editor/utils/error-handling").then((module) => {
            module.handleTRPCClientError(error);
          });
        }
      },
    }),
    // Note (Noah, 2024-05-27): This is how you define a global error handler in TRPC, see
    // https://tkdodo.eu/blog/react-query-error-handling#the-global-callbacks
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.reploIgnoreDefaultErrorHandling) {
          return;
        }
        if (error instanceof TRPCClientError) {
          // Note (Noah, 2025-10-03): We have to dynamically import this to avoid a circular dependency,
          // since error handling needs to import TRPC routers (which we're currently constructing)
          void import("@editor/utils/error-handling").then((module) => {
            module.handleTRPCClientError(error);
          });
        }
      },
    }),
  });
}

declare global {
  // Note (Noah, 2025-10-03): We save these on the window instead of in a global variable here to
  // avoid "used before assignment" errors
  interface Window {
    _reploNamespacedQueryClients?: Map<string, QueryClient>;
    _reploQueryClient?: QueryClient;
  }
}

export function getNamespacedQueryClient(namespace = "default"): QueryClient {
  let namespacedQueryClients = window._reploNamespacedQueryClients;
  if (!namespacedQueryClients) {
    window._reploNamespacedQueryClients = new Map<string, QueryClient>();
    namespacedQueryClients = window._reploNamespacedQueryClients;
  }
  const existing = namespacedQueryClients.get(namespace);
  if (existing) {
    return existing;
  }
  const client = createReploQueryClient();
  namespacedQueryClients.set(namespace, client);
  return client;
}

export function getQueryClient(): QueryClient {
  if (window._reploQueryClient) {
    return window._reploQueryClient;
  }
  window._reploQueryClient = createReploQueryClient();
  return window._reploQueryClient;
}
